import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PageErrorBoundaryProps {
  children: ReactNode;
}

interface PageErrorBoundaryState {
  error: Error | null;
}

/**
 * Isola erros de runtime a UMA página, em vez de derrubar o app inteiro (que é o que
 * o ErrorBoundary global em main.tsx faz — necessário como última rede, mas grosseiro
 * demais pro dia a dia). Usado dentro de `ProtectedRoute`, com `key={pathname}` no
 * ponto de uso, pra resetar sozinho ao trocar de rota (não precisa recarregar a página
 * pra sair do estado de erro).
 */
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  state: PageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <div>
            <h2 className="text-lg font-semibold">Não foi possível carregar esta página</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Algo deu errado aqui, mas o resto do sistema continua funcionando normalmente.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => (window.location.href = '/dashboard')}>
              Voltar ao Dashboard
            </Button>
            <Button onClick={() => window.location.reload()}>Recarregar página</Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
